import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Grid,
  Card,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  SwapHoriz as SwapIcon,
  Speed as SpeedIcon,
  CalendarToday,
  RateReview,
  LocalShipping,
  EditCalendar,
  Send,
  Sync,
  CheckCircleOutline,
  History
} from '@mui/icons-material';
import { DynamoDBClient, ScanCommand } from '@aws-sdk/client-dynamodb';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import TermsAndConditions from '../../components/TermsAndConditions';
import PrivacyNotice from '../../components/PrivacyNotice';
import logo from '../../static/logo_main_3124x512.png';
import { useThemeContext } from '../../contexts/ThemeManager/ThemeManager';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

export default function LandingPage() {
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useThemeContext();
  const [showImageModal, setShowImageModal] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isDarkMode) {
      toggleTheme()
    }
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const credentials = fromCognitoIdentityPool({
        identityPoolId: "us-east-1:381437dd-c20e-4c2e-b391-81ab6d137d7b",
        clientConfig: { region: "us-east-1" },
      });

      const client = new DynamoDBClient({
        region: "us-east-1",
        credentials: credentials,
      });

      const result = await client.send(new ScanCommand({
        TableName: "Team_Management_Reviews",
      }));

      if (result.Items && result.Items.length > 0) {
        const formattedReviews = result.Items.map((item: any) => {
          // Get first 2 characters of userId for avatar
          const userIdInitials = item.name?.S?.substring(0, 2).toUpperCase() || 'AU';
          const staff = item.staff?.S === 'owner' ? 'Business Owner' : 'Staff'

          return {
            name: item.name?.S || 'Anonymous User',
            role: staff,
            text: item.comment?.S || '',
            avatar: userIdInitials,
            rating: parseInt(item.review?.N || '0'),
            dateTime: item.dateTime?.S
          };
        });
        setReviews(formattedReviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Product', id: 'product' },
    { label: 'Features', id: 'features' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'About', id: 'about' },
    { label: 'Contact', id: 'contact' }
  ];

  const features = [
    {
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      title: 'Smart Scheduling',
      description: 'Create and manage staff schedules in minutes. Control shifts, positions, and staff availability.',
      color: '#2196F3'
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      title: 'Team Management',
      description: 'Organize your team, assign positions, and track staff availability all in one place.',
      color: '#9C27B0'
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 40 }} />,
      title: 'Smart Notifications',
      description: 'Automated shift reminders, schedule changes, and important updates sent via email and SMS.',
      color: '#FF9800'
    },
    {
      icon: <SwapIcon sx={{ fontSize: 40 }} />,
      title: 'Shift Swapping',
      description: 'Let staff request shift swaps with or without manager approval. Reduce last-minute scheduling headaches.',
      color: '#4CAF50'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Analytics & Insights',
      description: 'Track labor data, attendance patterns, and scheduling efficiency with powerful analytics powered by AI.',
      color: '#F44336'
    },
    {
      icon: <History sx={{ fontSize: 40 }} />,
      title: 'Time Off',
      description: 'Manage staff time-off requests and approvals seamlessly within the scheduling platform.',
      color: '#00BCD4'
    }
  ];

  const benefits = [
    {
      stat: 'Unified Schedule',
      label: 'No more spreadsheets or notes. A single place to create, manage, and order your staff schedules',
      icon: <CalendarToday sx={{ fontSize: 40, color: 'info.main' }} />
    },
    {
      stat: 'Highly Manageable',
      label: 'Makes it easier to adjust staff when needed to the places that need them. Make decisions driven by data',
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'info.main' }} />
    },
    {
      stat: 'Stay Coordinated',
      label: 'Keeps your team informed and up-to-date with changes in the schedule',
      icon: <NotificationsIcon sx={{ fontSize: 40, color: 'info.main' }} />
    }
  ];

  const pricingPlans = [
    {
      name: 'Basic',
      description: 'Manage 1 schedule, up to 5 staff',
      newForTier: '-',
      idealUser: 'Small teams',
      price: '$10',
      period: '/mo',
      features: [
        '1 schedule',
        'Up to 5 staff',
        'Email notifications',
        'Basic scheduling tools'
      ],
      highlighted: false
    },
    {
      name: 'Pro',
      description: 'Manage 10 schedules, up to 15 staff, templates, basic analytics',
      newForTier: '-',
      idealUser: 'Restaurants, shops',
      price: '$29',
      period: '/mo',
      features: [
        'Manage 10 schedules',
        'Up to 15 staff',
        'Schedule templates',
        'Basic analytics',
      ],
      highlighted: true
    },
    {
      name: 'Business',
      description: 'Unlimited schedules, up to 35 staff, templates, analytics',
      newForTier: '-',
      idealUser: 'Multi-site or 25+ staff',
      price: '$79',
      period: '/mo',
      features: [
        'Unlimited schedules',
        'Up to 35 staff',
        'Advanced analytics',
        'Templates & automation'
      ],
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: 'How long does it take to set up?',
      answer: 'You can be up and running in less than 10 minutes! Simply create your account, add your team members, and start scheduling. Our intuitive interface requires no training.'
    },
    {
      question: 'Can staff access their schedules on mobile?',
      answer: 'Yes! Stafflows is fully mobile-responsive. Staff can view schedules, request time off, and swap shifts from any device with a web browser.'
    },
    {
      question: 'What if I need to make last-minute schedule changes?',
      answer: 'Schedule changes are easy! Simply update the shift and all affected staff are notified instantly via email and SMS.'
    },
    {
      question: 'Is there a contract or can I cancel anytime?',
      answer: 'No contracts required! You can cancel your subscription at any time. We believe in earning your business every month.'
    },
    {
      question: 'Struggling to pay for staffing software?',
      answer: 'We want to work with you to make things affordable because we understand how helpful this software can be. Contact us directly if you\'re facing financial difficulties and we can discuss options'
    },
    {
      question: 'Do you offer customer support?',
      answer: 'Absolutely! All plans include email support. Professional and Enterprise plans get priority support, and Enterprise customers receive 24/7 phone support.'
    }
  ];

  return (
    <Box>
      {/* Navigation */}
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'primary.dark',
          bgcolor: 'background.paper',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, width: "25%" }}>
            <Box sx={{ display: { xs: 'flex', md: 'flex', paddingBottom: 1 } }}>
              <img
                src={logo}
                alt="Stafflows Logo"
                style={{
                  width: "25%"
                }}
              />
            </Box>

          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, mr: 3 }}>
            {navItems.map((item) => (
              <Button
                key={item.id}
                color="inherit"
                onClick={() => scrollToSection(item.id)}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                {item.label}
              </Button>
            ))}
            {reviews.length > 0 && (
              <Button
                color="inherit"
                onClick={() => scrollToSection('reviews')}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Reviews
              </Button>
            )}
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            <Button
              variant="outlined"
              href="/login"
              sx={{ textTransform: 'none' }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              href="/login"
              sx={{ textTransform: 'none' }}
            >
              Get Started Free
            </Button>
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            sx={{ display: { xs: 'flex', md: 'none' } }}
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              Stafflows
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton onClick={() => scrollToSection(item.id)}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
            {reviews.length > 0 && (
              <ListItem disablePadding>
                <ListItemButton onClick={() => scrollToSection('reviews')}>
                  <ListItemText primary="Reviews" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button variant="outlined" fullWidth href="/login">
              Sign In
            </Button>
            <Button variant="contained" fullWidth href="/login">
              Get Started Free
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0b75d1ff 0%, #fff5eae7 60%, #f39538e7 100%)',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          borderBottom: 2,
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                fontWeight={800}
                color='text.primary'
                gutterBottom
                sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}
              >
                Staff Scheduling Made Simple
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6 }}
              >
                Stop wasting hours on spreadsheets. Stafflows helps small businesses create regular schedules,
                manage teams, and keep everyone in sync—all in one intuitive platform.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="/login"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ textTransform: 'none', fontSize: '1.1rem', px: 4 }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection('product')}
                  sx={{ textTransform: 'none', fontSize: '1.1rem', px: 4 }}
                >
                  See How It Works
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 4, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleOutline color="primary" fontSize="medium" />
                  <Typography variant="body2" color="text.secondary" fontWeight={800}>
                    No credit card required
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleOutline color="primary" fontSize="medium" />
                  <Typography variant="body2" color="text.secondary" fontWeight={800}>
                    14-day free trial
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={10}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  transform: 'rotate(2deg)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'rotate(0deg)' }
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'white',
                    p: 3,
                    borderRadius: 4
                  }}
                >
                  <Box sx={{ bgcolor: 'grey.100', height: 300, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'zoom-in', '&:hover': { opacity: 0.8 } }} onClick={() => setShowImageModal(true)}>
                    <img
                      src={require('../../static/minnie_hair_salon.png')}
                      alt="Dashboard Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  {/* Placeholder for screenshot - replace with actual image */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                    Dashboard Preview
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                  <Typography variant="h3" fontWeight={700} color="primary" gutterBottom>
                    {benefit.stat}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Product Section */}
      <Box id="product" sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>
              How Stafflows Works
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Three simple steps to better workforce management
            </Typography>
          </Box>

          <Grid container spacing={12}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, '&:hover .avatar': { bgcolor: 'info.main', transition: 'background-color .3s' } }}>
                <Avatar
                  className="avatar"
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <EditCalendar sx={{ fontSize: 40, color: 'white' }} />
                </Avatar>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Create your Schedule
                </Typography>
                <Typography color="text.secondary">
                  Build a schedule that fits your business. Add staff, define positions, and set shift times with ease.
                  Easily customize for holidays, special events, and peak hours.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, '&:hover .avatar': { bgcolor: 'info.main', transition: 'background-color .3s' } }}>
                <Avatar
                  className="avatar"
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <Send sx={{ fontSize: 40, color: 'white' }} />
                </Avatar>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Invite your Team
                </Typography>
                <Typography color="text.secondary">
                  Send invitations via a link to your staff.
                  Staff can join and start viewing their schedules after they're assigned to shifts or positions
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, '&:hover .avatar': { bgcolor: 'info.main', transition: 'background-color .3s' } }}>
                <Avatar
                  className="avatar"
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <Sync sx={{ fontSize: 40, color: 'white' }} />
                </Avatar>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Stay in Sync
                </Typography>
                <Typography color="text.secondary">
                  Staff can regulate themselves with open positions and swapping shifts,
                  while managers can monitor staff requests and schedule maintenance.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box id="divider" sx={{ py: 14, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Divider variant='middle' />
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: 10, bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ marginBottom: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Everything You Need
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Powerful features designed for small business success
            </Typography>
          </Box>

          <Grid container spacing={12}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      bgcolor: `${feature.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: feature.color
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      {reviews.length > 0 && (
        <Box id="reviews" sx={{ py: 10, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="h3" fontWeight={800} gutterBottom>
                Loved by Business Owners
              </Typography>
              <Typography variant="h6" color="text.secondary">
                See what our customers have to say
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {reviews.map((testimonial, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ height: '100%', p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon
                          key={star}
                          sx={{
                            color: star <= testimonial.rating ? 'warning.main' : 'action.disabled',
                            fontSize: 20
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                      "{testimonial.text}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {testimonial.avatar}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {testimonial.name.split(" ")[0]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {testimonial.role}
                        </Typography> <br />
                        <Typography variant="caption" color="text.secondary">
                          {moment(testimonial.dateTime).format('MMMM Do, YYYY')}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: 10, bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg" sx={{ marginBottom: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>
              Simple, Transparent Pricing
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Choose the plan that fits your business
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="stretch">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    p: 4,
                    border: plan.highlighted ? 3 : 1,
                    borderColor: plan.highlighted ? 'primary.main' : 'divider',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    transform: plan.highlighted ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: plan.highlighted ? 6 : 1,
                    overflow: 'visible'
                  }}
                >
                  {plan.highlighted && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -15,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 600,
                        color: 'white'
                      }}
                    />
                  )}
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {plan.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
                    <Typography variant="h3" fontWeight="bold" color="info.light">
                      {plan.price}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {plan.period}
                    </Typography>
                  </Box>
                  <Button
                    variant={plan.highlighted ? 'contained' : 'outlined'}
                    size="large"
                    fullWidth
                    href="/login"
                    color={plan.highlighted ? 'info' : 'primary'}
                    sx={{ mb: 3, textTransform: 'none', color: plan.highlighted ? 'white' : 'primary.main' }}
                  >
                    {plan.price === 'Custom' ? 'Contact Sales' : 'Start Free Trial'}
                  </Button>
                  <List sx={{ flex: 1 }}>
                    {plan.features.map((feature, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                        <CheckCircleIcon
                          sx={{ mr: 1, color: 'success.main', fontSize: 20 }}
                        />
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>
              Frequently Asked Questions
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Everything you need to know
            </Typography>
          </Box>

          {faqs.map((faq, index) => (
            <Accordion key={index} elevation={0} sx={{ mb: 1, '&:before': { display: 'none' }, boxShadow: 1, backgroundColor: 'white' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about" sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>
              About Stafflows
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph sx={{ mt: 3 }}>
              We built Stafflows because we know how frustrating staff scheduling can be.
              After years of wrestling with spreadsheets and juggling last-minute changes,
              we knew there had to be a better way.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Our mission is simple: help small business owners spend less time on scheduling
              and more time growing their business. We believe workforce management should be
              intuitive, accessible, and possibly even enjoyable to use.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Stafflows was built by a single person with the purpose of making business, specifically staff, management that much easier for
              an affordable cost. We're dedicated to continuous improvement and customer satisfaction.
              Your feedback drives our development roadmap, and we're always here to help.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box id="divider" sx={{ py: 12, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Divider variant='middle' />
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact" sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="md" sx={{ marginBottom: 5 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" fontWeight={800} gutterBottom>
              Get in Touch
            </Typography>
            <Typography variant="h6" color="text.secondary">
              We'd love to hear from you
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <EmailIcon sx={{ fontSize: 48, color: 'info.light', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Email Us
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  We'll respond within 24 hours
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  app.development.cosmos@gmail.com
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%', cursor: 'pointer' }} onClick={() => {
                navigate('/login')
              }}>
                <RateReview sx={{ fontSize: 48, color: 'info.light', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Leave a Review
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Let us know how you're enjoying Stafflows
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <LocalShipping sx={{ fontSize: 48, color: 'info.light', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Mail Us
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  502 W 7th ST
                  <br />
                  STE 100
                  <br />
                  Erie, PA, 16502, USA
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 10,
          background: 'linear-gradient(135deg, #1761a1ff 0%, #0b75d1ff 100%)',
          color: 'white',
          textAlign: 'center',
          borderTop: 2,
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={800} gutterBottom>
            Ready to Simplify Your Scheduling?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join hundreds of businesses already using Stafflows
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              href="/login"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                textTransform: 'none',
                fontSize: '1.1rem',
                px: 4,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Start Your Free Trial
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
            <img
              src={logo}
              alt="Stafflows Logo"
              style={{
                width: "25%",
                filter: 'invert(1)'
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 4 }}>
            <PrivacyNotice color={'white'} />
            <TermsAndConditions color={'white'} />
          </Box>
        </Container>
      </Box>

      {/* Dashboard Preview Image Modal */}
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 2
          }
        }}
      >
        <Box sx={{ position: 'relative', bgcolor: 'background.paper', p: 2 }}>
          <IconButton
            onClick={() => setShowImageModal(false)}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.primary',
              '&:hover': {
                bgcolor: alpha(theme.palette.info.main, 0.1)
              }
            })}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <img
              src={require('../../static/minnie_hair_salon.png')}
              alt="Dashboard Preview"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}